import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Constants from '@/helpers/constants';
import { getEditorId, removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { setHelpAttachments } from '@/redux/Help/help.action';
import { getHelpAttachments } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';
import styles from '../HelpText.module.scss';

interface AttachmentsHelpTextProps {
  helpAttachments: ReduxHelp;
  setAttachments: (help: ReduxHelp) => void;
}

const AttachmentsHelpText: React.FC<AttachmentsHelpTextProps> = (props) => {
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const [originalValues, setOriginalValues] = useState({});
  const { helpAttachments, setAttachments } = props;
  const isLoading = helpAttachments && !helpAttachments.ATTACHMENTS_TAB;
  const { addToast } = useToastContext();
  const ATTACHMENTS_TAB = 'ATTACHMENTS_TAB';
  const ATTACHMENTS_ATTACHMENTS = 'ATTACHMENTS_ATTACHMENTS';

  const ID_GENERAL_ATTACHMENTS_TAB = 'general-attachments-tab';
  const ID_GENERAL_ATTACHMENTS = 'general-attachments';

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpAttachments && helpAttachments.modified ? helpAttachments : data;
        setAttachments(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, label, data) => {
    const propValues = { ...helpAttachments[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label][lang]);
    const newState = {
      ...helpAttachments,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setAttachments(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_GENERAL_ATTACHMENTS_TAB}
              title={t('helptext')}
              initialValue={helpAttachments?.ATTACHMENTS_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, ATTACHMENTS_TAB, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('attachments.title')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_GENERAL_ATTACHMENTS}
              title={t('helptext')}
              initialValue={helpAttachments?.ATTACHMENTS_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, ATTACHMENTS_ATTACHMENTS, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpAttachments: getHelpAttachments(state),
});

const actionCreators = {
  setAttachments: setHelpAttachments,
};

export default connect(mapStateToProps, actionCreators)(AttachmentsHelpText);
